import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, from, forkJoin } from 'rxjs';
import { HttpClient, HttpHeaders, HttpErrorResponse } from '@angular/common/http';
import { catchError, switchMap, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { AssetsDataService } from './assets-data.service';
import { NewLog } from '../models/logs/NewLog';
import { EntraService } from './entra.service';
import { ToastService } from './toast.service';
import { handleErrorFunction } from '../shared/helpers/handleError';
import { PrintRequest, PrintFromLogRequest } from '../models/printing/PrintRequest';
import { PrintResult } from '../models/printing/PrintResult';
import { PrinterInfo, PrintersResult } from '../models/printing/PrinterInfo';
import { ReceivingItemRef } from '../models/logs/ReceivingItemRef';
// Ships with the barcode scanner this app already uses, so the per-item QR codes
// add no dependency. Marked deprecated in favour of @zxing/browser (also
// installed); moving is a swap of this import when the old one finally goes.
import { BrowserQRCodeSvgWriter } from '@zxing/library';
import { buildQrUrl, normalizeCopies } from '../domain/receiving-log/rules/qr-items.rules';
import { itemCaption, itemCaptionAlwaysCounted } from '../domain/receiving-log/rules/container-items.rules';
import {
  PrintDoc,
  PrintFallbackMode,
  printDocsFor,
  isTransportLevelFailure,
  modeForFailedDocs,
  combineFallbackModes,
} from '../domain/receiving-log/rules/print-path.rules';
import { PrintFallbackService } from './print-fallback.service';
import { PrintRouteContextService } from './print-route-context.service';
import { todaysDateString } from '../utils/date-functions';


/**
* PrintService handles all printing functionality for the application.
* This includes printing Dymo and large labels with specific content and style configurations.
* 
* It is utilized by the LogFormComponent to print labels for shipments.
* 
* The service is responsible for populating templates with label data, generating print content, 
* and managing the printing process via iframes for proper layout.
* 
* The templates and styles for Dymo and large labels are provided by the AssetsDataService.
* 
* 
*
*/


/**
 * The per-item template slots, filled AFTER {@link PrintService.populateTemplate}
 * has run over the template.
 *
 * Deliberately not named after any `NewLog` key: that method loops over those
 * keys and would blank a colliding slot before the item filler ever saw it. It
 * also means the spliced QR markup is never exposed to its `{{key}}` regex.
 */
const ITEM_QR_SLOT = /{{itemQr}}/g;
const CONTAINER_CAPTION_SLOT = /{{containerCaption}}/g;

/**
 * Escapes text spliced into label markup. The container type is operator-entered
 * free text on the desktop form, so it reaches the label unvetted.
 */
function escapeLabelText(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/**
 * The per-container context a browser-printed label set needs: one label per
 * physical item, each carrying its own QR code and its own "BOX n of N" caption.
 *
 * Omitted — or carrying no usable rows — the print falls back to the legacy
 * behaviour of one identical label repeated by the uniform quantity with no QR,
 * which is all a log predating item rows can print.
 */
export interface BrowserLabelItems {
  /**
   * The log's stored item rows, one per physical container. `typeIndex` and
   * `typeTotal` are printed verbatim, never derived from array position: a mixed
   * delivery numbers each type separately, and a partial reprint of one item has
   * no array to count against yet must still read "BOX 2 of 3".
   */
  items: ReceivingItemRef[];

  /**
   * Copies of EACH item's document, by item index — the same meaning the print
   * API's `qr.items[].copies` carries. Both paths have to turn one set of
   * quantities into the same number of labels, or a printer outage would change
   * how much gets printed as well as where. Missing entries default to 1; zero
   * omits that item.
   */
  copies?: number[];

  /**
   * The app base URL the QR payload URL is built on — {@link QR_BASE_URL} at
   * every call site. Passed in rather than read from `environment` here so this
   * service stays testable without stubbing the environment module.
   */
  appBaseUrl: string;

  /**
   * Whether these labels carry QR codes at all. Facility configuration, from
   * {@link browserQrEnabled} — anything printed through the print API carries QR
   * codes unconditionally, and this either-or belongs only to the browser path.
   *
   * Turning it off suppresses the QR square ALONE. The per-container labels and
   * their "BOX n of N" captions still print, because those answer "which box is
   * this" — a question that does not stop mattering at a facility with nothing to
   * scan the code with.
   *
   * Defaults to on when unset, so a caller that has not been taught about the
   * setting keeps printing what it printed before.
   */
  includeQr?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class PrintService {

  private isPrintingLargeLabel: boolean = false;
  private isPrintingDymoLabel: boolean = false;
  public printingLoading = new BehaviorSubject<boolean>(false);
  private _assetsDataService: AssetsDataService;
  private _entraService: EntraService;
  private _httpClient: HttpClient;
  private _toastService: ToastService;
  private _printFallbackService: PrintFallbackService;
  private _printRouteContextService: PrintRouteContextService;
  private isDymoInitialized: boolean = false;

  /**
   * The QR size requested from the encoder, in CSS pixels.
   *
   * NOT the printed size — each label stylesheet's calibration block sets that.
   * This only has to be large enough that the encoder picks a whole-number module
   * size, because it floors `requested / modules` and a too-small request quietly
   * shrinks the code. 240 is comfortably above what a ~100-character URL needs at
   * any version it lands on.
   */
  private static readonly QR_SOURCE_PX = 240;

  /**
   * The print iframe's page box, in CSS pixels — US Letter at the CSS reference
   * 96dpi, which is the unit `in` resolves to in a browser regardless of what the
   * printer's own resolution is.
   *
   * Only ever used for MEASURING: the printer decides the real page size from the
   * user's print dialog. Sizing the iframe to it is what makes the fit pass see
   * roughly what will come out, instead of measuring a 300x150 default nobody prints.
   */
  private static readonly CSS_PX_PER_IN = 96;
  private static readonly PAGE_WIDTH_PX = 8.5 * PrintService.CSS_PX_PER_IN;
  private static readonly PAGE_HEIGHT_PX = 11 * PrintService.CSS_PX_PER_IN;

  /**
   * Floors and step sizes for the full-sheet fit pass.
   *
   * `MIN_LINE_PX` is a readability floor, not a layout one: the sheet is read off
   * a pallet across a warehouse aisle, and text small enough to need walking up to
   * has already failed at the job the sheet exists for.
   *
   * `MIN_QR_IN` is a scanning floor. A 1.25in code carries a ~60-character URL at
   * a module size a phone camera resolves from arm's length on a laser print;
   * below that the failures start being intermittent, which is worse than a code
   * that plainly does not fit.
   */
  /**
   * How long {@link printContentInIframe} waits for the iframe's load event
   * before printing anyway. See the comment there: the event can fire before the
   * handler is attached, and a missed one used to mean no dialog at all.
   */
  private static readonly PRINT_FALLBACK_MS = 400;

  private static readonly LINE_STEP_PX = 2;
  private static readonly MIN_LINE_PX = 28;
  private static readonly QR_STEP_IN = 0.125;
  private static readonly MIN_QR_IN = 1.25;

  /** Printers reported by the print API, for the optional printer-override UI. */
  public availablePrinters = new BehaviorSubject<PrinterInfo[]>([]);
  public printersLoading = new BehaviorSubject<boolean>(false);

  constructor(

    assetsDataService: AssetsDataService,
    entraService: EntraService,
    httpClient: HttpClient,
    toastService: ToastService,
    printFallbackService: PrintFallbackService,
    printRouteContextService: PrintRouteContextService,
  ) {
    this._assetsDataService = assetsDataService;
    this._entraService = entraService;
    this._httpClient = httpClient;
    this._toastService = toastService;
    this._printFallbackService = printFallbackService;
    this._printRouteContextService = printRouteContextService;
  }


//## Print API ####################################################
/**
 * Submits a print job to the print API and surfaces the result to the user.
 *
 * Unlike the browser-printing methods above (which render labels client-side via
 * an iframe), this routes the job through the backend `POST {apiUrl}/V1/print`
 * endpoint so the configured Zebra / Copier printers handle it server-side.
 *
 * The endpoint returns 200 OK even on partial or total print failure, so this
 * inspects the `PrintResult` body: a success toast on `overallSuccess`, otherwise
 * a per-document warning built from the failed `outcomes`.
 *
 * This method is called within the relevant components when the user submits a
 * print job for a receiving log.
 *
 * On a transport-level failure (the call never reached the print API, or it
 * reported total failure) this offers the operator the automatic fallback:
 * re-render the failed document(s) in the browser now, and/or turn on the
 * matching outage mode for future jobs on this device. See
 * {@link offerFallback}. `printRequest` carries everything the browser path
 * needs (job/PO/vendor/etc.), so the re-render is built here with no help
 * from the caller.
 *
 * @param printRequest The print job to submit (kind + the relevant receiving fields and printer name(s)).
 * @param onSuccessFunction Optional callback executed when every attempted document printed successfully.
 */
public submitPrintJob(
  printRequest: PrintRequest,
  onSuccessFunction?: Function
): void {
  this.printingLoading.next(true);

  this.SubmitPrint(printRequest).subscribe({
    next: (result: PrintResult) => {
      this.handlePrintResult(result, onSuccessFunction);
    },
    error: (err) => {
      console.error('Error submitting print job:', err);
      this._toastService.showWarningMessage(err?.message ?? 'Unable to submit the print job. Please try again.');
      this.printingLoading.next(false);
      this.offerFallback(
        printDocsFor(printRequest.kind),
        err,
        () => this.renderPrintRequestInBrowser(printRequest, printDocsFor(printRequest.kind)),
      );
    },
    complete: () => {
      this.printingLoading.next(false);
    },
  });
}

/**
 * Submits several print jobs as one user-facing action, surfacing a single
 * combined result. Used by the QR print flow, which splits a "Both" job into
 * separate Zebra/Copier requests when the per-item label and sheet quantities
 * differ (one request can only carry one per-item copies value).
 *
 * All requests are fired together (forkJoin); the outcomes are merged and
 * handled exactly like a single job — one success toast, or one warning
 * listing every failed document. A single-request batch defers to
 * {@link submitPrintJob} unchanged.
 *
 * @param printRequests The print jobs to submit together.
 * @param onSuccessFunction Optional callback executed when every attempted document printed successfully.
 */
public submitPrintJobBatch(
  printRequests: PrintRequest[],
  onSuccessFunction?: Function
): void {
  if (printRequests.length === 0) { return; }
  if (printRequests.length === 1) {
    this.submitPrintJob(printRequests[0], onSuccessFunction);
    return;
  }

  this.printingLoading.next(true);

  forkJoin(printRequests.map((request) => this.SubmitPrint(request))).subscribe({
    next: (results: PrintResult[]) => {
      const merged: PrintResult = {
        overallSuccess: results.every((result) => result.overallSuccess),
        outcomes: results.flatMap((result) => result.outcomes ?? []),
      };
      this.handlePrintResult(merged, onSuccessFunction);
    },
    error: (err) => {
      console.error('Error submitting print job batch:', err);
      this._toastService.showWarningMessage(err?.message ?? 'Unable to submit the print job. Please try again.');
      this.printingLoading.next(false);
      // forkJoin surfaces the first failure and cancels the rest, so which
      // request actually failed is unknown — the union of every document the
      // batch attempted is the conservative, honest answer.
      const docs = printRequests.flatMap((request) => printDocsFor(request.kind));
      this.offerFallback(
        docs,
        err,
        () => printRequests.forEach((request) => this.renderPrintRequestInBrowser(request, printDocsFor(request.kind))),
      );
    },
    complete: () => {
      this.printingLoading.next(false);
    },
  });
}

/**
 * Submits a print job for an already-stored receiving log via
 * `POST {apiUrl}/V1/print/from-log/{logId}`, surfacing the result to the user.
 *
 * The server fills the document's PO / job / vendor / items / etc. from the
 * stored log, so the request body is intent-only (kind + printer name(s), plus
 * any optional overrides). This is the path for print-on-receive (fired after a
 * successful submit, once the `receivingLogId` is known) and later reprints.
 *
 * Like {@link submitPrintJob}, the endpoint returns 200 OK even on partial or
 * total failure, so this inspects the `PrintResult` body rather than the status.
 *
 * Unlike {@link submitPrintJob}, this request is intent-only — it carries none
 * of the job/PO/vendor data a browser-rendered label needs, because the print
 * API fills that in from the stored log. This service has no way to rebuild
 * that on a failure, so the browser re-render offer (see
 * {@link offerFallback}) is delegated to `onRenderInBrowser`: the caller
 * already has the stored log's data (it built the intent from the same row),
 * so it renders the browser fallback itself when the operator accepts.
 *
 * @param logId The id of the stored receiving log to print from.
 * @param request The print intent (kind + printer name(s) + optional overrides).
 * @param onSuccessFunction Optional callback executed when every attempted document printed successfully.
 * @param onRenderInBrowser Optional callback, invoked with the documents that
 *   failed, if the operator accepts the offer to print them from this
 *   browser instead. Omit where the caller has no browser fallback to offer.
 */
public submitPrintJobFromLog(
  logId: number,
  request: PrintFromLogRequest,
  onSuccessFunction?: Function,
  onRenderInBrowser?: (docs: PrintDoc[]) => void,
): void {
  this.printingLoading.next(true);

  this.SubmitPrintFromLog(logId, request).subscribe({
    next: (result: PrintResult) => {
      this.handlePrintResult(result, onSuccessFunction);
    },
    error: (err) => {
      console.error('Error submitting print job from log:', err);
      this._toastService.showWarningMessage(err?.message ?? 'Unable to submit the print job. Please try again.');
      this.printingLoading.next(false);
      const docs = printDocsFor(request.kind);
      this.offerFallback(docs, err, onRenderInBrowser ? () => onRenderInBrowser(docs) : undefined);
    },
    complete: () => {
      this.printingLoading.next(false);
    },
  });
}

/**
 * The from-log counterpart of {@link submitPrintJobBatch}: fires several
 * `from-log/{logId}` requests together (forkJoin) and merges the outcomes into
 * one result — used when a per-item reprint splits a `Both` job into separate
 * Zebra/Copier requests (an item's label count ≠ its sheet count). A
 * single-request batch defers to {@link submitPrintJobFromLog} unchanged.
 *
 * @param logId The id of the stored receiving log to print from.
 * @param requests The from-log jobs to submit together.
 * @param onSuccessFunction Optional callback executed when every attempted document printed successfully.
 * @param onRenderInBrowser Optional callback, invoked with the documents that
 *   failed (the union across the batch — see {@link submitPrintJobBatch}), if
 *   the operator accepts the offer to print them from this browser instead.
 */
public submitPrintJobFromLogBatch(
  logId: number,
  requests: PrintFromLogRequest[],
  onSuccessFunction?: Function,
  onRenderInBrowser?: (docs: PrintDoc[]) => void,
): void {
  if (requests.length === 0) { return; }
  if (requests.length === 1) {
    this.submitPrintJobFromLog(logId, requests[0], onSuccessFunction, onRenderInBrowser);
    return;
  }

  this.printingLoading.next(true);

  forkJoin(requests.map((request) => this.SubmitPrintFromLog(logId, request))).subscribe({
    next: (results: PrintResult[]) => {
      const merged: PrintResult = {
        overallSuccess: results.every((result) => result.overallSuccess),
        outcomes: results.flatMap((result) => result.outcomes ?? []),
      };
      this.handlePrintResult(merged, onSuccessFunction);
    },
    error: (err) => {
      console.error('Error submitting print job from log batch:', err);
      this._toastService.showWarningMessage(err?.message ?? 'Unable to submit the print job. Please try again.');
      this.printingLoading.next(false);
      const docs = requests.flatMap((request) => printDocsFor(request.kind));
      this.offerFallback(docs, err, onRenderInBrowser ? () => onRenderInBrowser(docs) : undefined);
    },
    complete: () => {
      this.printingLoading.next(false);
    },
  });
}

/**
 * Shared handling of a `PrintResult`: a success toast when every attempted
 * document printed, otherwise a per-document warning built from the failures.
 */
private handlePrintResult(result: PrintResult, onSuccessFunction?: Function): void {
  if (result.overallSuccess) {
    this._toastService.showSuccessMessage('Sent to printer.');
    if (onSuccessFunction) {
      onSuccessFunction();
    }
  } else {
    const failed = result.outcomes.filter((outcome) => !outcome.success);
    const failureMessage = failed
      .map((outcome) => `${outcome.kind}: ${outcome.message}`)
      .join('\n');
    this._toastService.showWarningMessage(failureMessage || 'Print job failed.', 6000);
  }
}

/**
 * Offers the operator the automatic fallback for a backend print call that
 * never came back as a `PrintResult` at all (network error, timeout, or a
 * total failure) — the app already knows the printer or the API is unreachable,
 * so it offers to act on that instead of only logging it.
 *
 * TWO INDEPENDENT OFFERS, both behind an explicit button — never applied on
 * their own. Setting the mode silently would route an operator's whole shift
 * to a desk Dymo over one failure that had nothing to do with the printer;
 * re-rendering silently would open a print dialog the operator did not ask
 * for. See `docs/printing.md`, "Automatic offer on failure".
 *
 *   1. Re-render the failed document(s) in this browser, right now — only
 *      offered where `renderInBrowser` is given AND this is not mobile: a
 *      phone's print dialog reaches no printer, so the offer would be a
 *      button that does nothing.
 *   2. Turn on the fallback mode this failure implies (see
 *      {@link modeForFailedDocs}), combined with whatever mode this device
 *      already has stored (see {@link combineFallbackModes}) — omitted when
 *      that combination would not actually change anything.
 *
 * @param docs Every document the failed request(s) asked the backend to
 *   print (see {@link printDocsFor}) — a single request's kind, or the union
 *   across a batch.
 * @param err The error handleErrorFunction produced; `err.originalError` is
 *   the raw `HttpErrorResponse`, read for its status code.
 * @param renderInBrowser Renders `docs` in this browser, if this device can
 *   act on the offer. Undefined when the caller has no browser fallback to
 *   offer (see {@link submitPrintJobFromLog}).
 */
private offerFallback(docs: PrintDoc[], err: any, renderInBrowser?: () => void): void {
  const statusCode: number = err?.originalError?.status ?? 0;
  const failureMode = modeForFailedDocs(docs, isTransportLevelFailure(statusCode));
  if (failureMode === 'normal') { return; }

  // Not read from ScreenSizeService directly — the shared context provider is
  // the one place this app decides "is this mobile", so every screen (and now
  // this service) answers the question the same way.
  const isMobile = this._printRouteContextService.forLocation(undefined).isMobile;
  const docNames = docs.includes('label') && docs.includes('sheet')
    ? 'Labels and copier sheets'
    : docs.includes('label') ? 'Labels' : 'Copier sheets';

  const actions: { text: string; handler: () => void }[] = [];
  if (!isMobile && renderInBrowser) {
    actions.push({ text: 'Print from this browser now', handler: renderInBrowser });
  }

  const currentMode = this._printFallbackService.mode;
  const nextMode = combineFallbackModes(currentMode, failureMode);
  if (nextMode !== currentMode) {
    actions.push({
      text: `Route around it (${this.fallbackModeLabel(nextMode)})`,
      handler: () => this._printFallbackService.setMode(nextMode),
    });
  }

  if (actions.length === 0) { return; }

  const mobileNote = isMobile
    ? ' Print them from the log table on a desktop computer.'
    : '';
  this._toastService.showActionMessage(
    `${docNames} could not reach the printer.${mobileNote} `
      + 'Print them from this browser, and/or route future jobs around the printer until you turn it off?',
    actions,
    'warning',
  );
}

/**
 * Short label for the button that turns FAILUREMODE on — names what changes,
 * not the switch's internal name, so the operator reads what they are about
 * to do rather than jargon from `PrintFallbackMode`.
 */
private fallbackModeLabel(mode: PrintFallbackMode): string {
  switch (mode) {
    case 'zebraDown':
      return 'labels print from a desktop computer only until you turn it off';
    case 'copierDown':
      return 'copier sheets print from a desktop computer only until you turn it off';
    case 'printApiDown':
      return 'labels and copier sheets print from a desktop computer only until you turn it off';
    default:
      return '';
  }
}

/**
 * Rebuilds enough of a {@link NewLog} to render a browser fallback label/sheet
 * from a `PrintRequest` that failed on the backend.
 *
 * The browser templates only read `jobNumber`, `poNumber`, `jobName`,
 * `vendor`, `qtyUnits`, `contact`, `shelfLocation` and `dateIn` (the
 * `{{...}}` slots in dymo-template.html / large-label-template.html) — the
 * rest of `PrintRequest` (`jcDept`, `notify`, `items`, `notes`, `shopSqFt`,
 * `estimatedDateOut`) is copier-API-only and has nothing to fill on a
 * browser-rendered document, so it is not carried over.
 *
 * Deliberately renders WITHOUT the per-item QR block: `request.qr.items[]`
 * carries each item's already-finished URL, not the item GUID the browser
 * path encodes itself, and reverse-parsing that URL to recover the GUID would
 * tie this recovery path to the exact shape {@link buildQrUrl} happens to
 * produce today. The request's uniform quantities still apply, so the
 * operator gets the right NUMBER of a plain, identifiable label rather than
 * none at all — the same trade the legacy no-item-rows path already makes.
 */
private newLogFromPrintRequest(request: PrintRequest): NewLog {
  return new NewLog({
    poNumber: request.poNumber || '',
    vendor: request.vendor,
    jobNumber: request.jobNumber,
    jobName: request.jobName,
    qtyUnits: request.qtyUnits,
    shelfLocation: request.shelfLocation || '',
    contact: request.contact,
    dateIn: request.dateIn || todaysDateString(),
    printQtyDymo: request.labelCount ?? 1,
    printQtySheet: request.copyCount ?? 1,
  });
}

/** Renders the DOCS of a failed `PrintRequest` in this browser — see {@link newLogFromPrintRequest}. */
private renderPrintRequestInBrowser(request: PrintRequest, docs: PrintDoc[]): void {
  const labelData = this.newLogFromPrintRequest(request);
  if (docs.includes('label')) { this.printDymoLabel(labelData); }
  if (docs.includes('sheet')) { this.printLargeLabel(labelData); }
}


/** ##############################################################################################################################
 * HTTP request functions that make API calls to the backend print endpoint.
 * These functions handle the actual HTTP requests, acquiring an access token from
 * `EntraService` (delegated user token, scope `RM.ReadWrite`) before making the request.
 */

/**
 * HTTP Request to submit a print job.
 * Makes a POST request to `{apiUrl}/V1/print` with the print request as camelCase JSON.
 *
 * The token is acquired the same way as the other write/submit calls (e.g. receiving-log,
 * send-email); the frontend does not need to know about the PrintingAPI's own scope or role.
 *
 * @param printRequest The print job to submit.
 * @returns An observable that resolves to the print result (one outcome per document attempted).
 */
public SubmitPrint(printRequest: PrintRequest): Observable<PrintResult> {
  return from(this._entraService.acquireToken()).pipe(
    switchMap((accessToken) => {
      const url = `${environment.apiUrl}/V1/print`;
      const headers: HttpHeaders = new HttpHeaders({
        Authorization: `Bearer ${accessToken}`,
      });

      return this._httpClient
        .post<PrintResult>(url, printRequest, {
          headers: headers,
        })
        .pipe(catchError((error: HttpErrorResponse) => handleErrorFunction(error)));
    })
  );
}

/**
 * HTTP Request to submit a print job for a stored receiving log.
 * Makes a POST request to `{apiUrl}/V1/print/from-log/{logId}` with the intent
 * body as camelCase JSON, acquiring the delegated token the same way as the
 * other write/submit calls.
 *
 * @param logId The id of the stored receiving log to print from.
 * @param request The print intent (kind + printer name(s) + optional overrides).
 * @returns An observable that resolves to the print result (one outcome per document attempted).
 */
public SubmitPrintFromLog(logId: number, request: PrintFromLogRequest): Observable<PrintResult> {
  return from(this._entraService.acquireToken()).pipe(
    switchMap((accessToken) => {
      const url = `${environment.apiUrl}/V1/print/from-log/${logId}`;
      const headers: HttpHeaders = new HttpHeaders({
        Authorization: `Bearer ${accessToken}`,
      });

      return this._httpClient
        .post<PrintResult>(url, request, {
          headers: headers,
        })
        .pipe(catchError((error: HttpErrorResponse) => handleErrorFunction(error)));
    })
  );
}


/**
 * Fetches the list of printers the print API knows about and publishes it on
 * `availablePrinters`. Used to let the user optionally override the per-facility
 * default printer when submitting a print job. Failures degrade gracefully to an
 * empty list (the location-config default is still used).
 */
public fetchPrinters(): void {
  this.printersLoading.next(true);
  this.GetPrinters().subscribe({
    next: (printers: PrinterInfo[]) => {
      this.availablePrinters.next(printers ?? []);
    },
    error: (err) => {
      console.error('Error fetching printers:', err);
      this.availablePrinters.next([]);
      this.printersLoading.next(false);
    },
    complete: () => {
      this.printersLoading.next(false);
    },
  });
}

/**
 * HTTP Request to list available printers.
 * Makes a GET request to `{apiUrl}/V1/print/printers`, acquiring the delegated
 * token the same way as the other print/write calls.
 *
 * The endpoint returns a `PrintersResult` envelope (success/count metadata plus
 * the array), so this maps the body down to `printers` for callers.
 *
 * @returns An observable that resolves to the available printers.
 */
public GetPrinters(): Observable<PrinterInfo[]> {
  return from(this._entraService.acquireToken()).pipe(
    switchMap((accessToken) => {
      const url = `${environment.apiUrl}/V1/print/printers`;
      const headers: HttpHeaders = new HttpHeaders({
        Authorization: `Bearer ${accessToken}`,
      });

      return this._httpClient
        .get<PrintersResult>(url, { headers: headers })
        .pipe(
          map((result) => result?.printers ?? []),
          catchError((error: HttpErrorResponse) => handleErrorFunction(error))
        );
    })
  );
}


//## Dymo label printing ####################################################


/**
   * Prints a Dymo label using HTML and template data.
   * @param labelData - The data to be inserted into the label template 
   * @param itemContext - The log's container rows and per-item copies. Present:
   *   one label per physical container, each with its own QR code and "BOX n of
   *   N" caption. Absent: the legacy repeat-one-label-N-times document, no QR.
   */

  public printDymoLabel(labelData: NewLog, itemContext?: BrowserLabelItems): void {

    // Prevent multiple print requests simultaneously. Announced rather than
    // silent: a suppressed print looks exactly like a print that never happened,
    // and this method's first log line is below the guard, so a return here used
    // to leave no trace at all.
    if (this.isPrintingDymoLabel) {
      console.warn('Dymo label print ignored — one is already in flight.');
      return;
    }

     // Fetch Dymo label HTML and CSS from the assets service.
    const {css, html}= this._assetsDataService.GetDymoLabelAssets();
    const labelTemplate = html;
    const labelStyles = css;
    this.isPrintingDymoLabel = true;

    // The reset is scheduled in a `finally`, so a throw between here and there
    // cannot leave the flag stuck on. It used to sit after the render calls: one
    // failed render left this document type permanently suppressed for the life
    // of the page, and every later attempt returned at the guard above without
    // printing, logging, or erroring.
    try {
      //Populate template with label data and generate print content.
      const printHtmlPage = this.populateTemplate(labelTemplate, labelData, true);
      const printContent = this.generatePrintContent(printHtmlPage, labelData.printQtyDymo, itemContext);

      // Print content in an iframe.
      this.printContentInIframe(printContent, labelStyles, true);
    } finally {
      this.resetDymoLabelPrintingFlagAfterDelay();
    }

  }







//## Large label printing ####################################################



  /**
   * Main function to print large labels, populating them with provided data.
   * @param labelData - The data to be inserted into the large label template.
   * @param itemContext - The log's container rows and per-item copies; see
   *   {@link printDymoLabel}. The full sheet carries the same QR and caption.
   */

  public printLargeLabel(labelData: NewLog, itemContext?: BrowserLabelItems): void {
    // See printDymoLabel: announced rather than silent, because the log line
    // below this guard is what tells anyone a copier sheet was even attempted.
    if (this.isPrintingLargeLabel) {
      console.warn('Copier sheet print ignored — one is already in flight.');
      return;
    }
    console.log('Initiating large label print with data:', labelData);

    // Fetch large label HTML and CSS from the assets service — the QR-carrying
    // template/styles when this job has item rows and QR is on, otherwise the
    // QR-less twin. See usesQrSheet.
    const useQrSheet = this.usesQrSheet(itemContext);
    const {css, html}= this._assetsDataService.GetFullSheetAssets(useQrSheet);
    const labelTemplate = html;
    const labelStyles = css;
    this.isPrintingLargeLabel = true;

    // `finally`, so a throw in the render cannot leave the flag stuck on and
    // silently suppress every copier sheet for the rest of the page's life.
    try {
      //Populate template with label data and generate print content.
      const printHtmlPage = this.populateTemplate(labelTemplate, labelData);
      const printContent = this.generatePrintContent(printHtmlPage, labelData.printQtySheet, itemContext, useQrSheet);

      // Print content in an iframe.
      this.printContentInIframe(printContent, labelStyles);
    } finally {
      this.resetLargeLabelPrintingFlagAfterDelay();
    }

  }




  
//## Template Population ####################################################
  /**
   * Populates the label template with provided data, replacing placeholders with actual values.
   * @param template - The label's HTML template with placeholders (e.g., `{{key}}`).
   * @param labelData - The data object that contains the values to replace placeholders.
   * @param isDymoLabel - Optional flag to indicate if this is a Dymo label (for special handling).
   * @returns Populated HTML template.
   */

  private populateTemplate(template: string, labelData: NewLog, isDymoLabel:boolean=false): string {
    let populatedTemplate = template;

    // Loop through all properties in the labelData object and replace placeholders in the template.
    for (const key in labelData) {
      if (labelData.hasOwnProperty(key)) {

          const regex = new RegExp(`{{${key}}}`, 'g');
            const value = labelData[key as keyof NewLog] ?? ""; // Default to an empty string if value is null or undefine
          populatedTemplate = populatedTemplate.replace(regex, value as string);
      }}

          // If it's a Dymo label, handle the special {{logoPath}} replacement.
      if (isDymoLabel) {
        const regex = new RegExp(`{{logoPath}}`, 'g');
        const timeStamp = Date.now();
        const value = `assets/logo.png?${timeStamp}`; 
        populatedTemplate = populatedTemplate.replace(regex, value as string);

      }

    return populatedTemplate;
  }




  //## Shared printing Methods ####################################################

  /**
   * Builds the complete print document: ONE LABEL PER PHYSICAL CONTAINER, each
   * carrying its own QR code and its own "BOX n of N" caption, page-broken into
   * a single continuous document.
   *
   * This replaces repeating one identical label N times, which gave a three-box
   * delivery three interchangeable stickers that named no particular box and
   * carried nothing scannable. It matters most when the print API or the Zebra is
   * down: the browser path is then the PRIMARY label path, so a label with no
   * container identity would be worse than the outage it routes around.
   *
   * The legacy repeat path survives for a log with no item rows — an older
   * submission that never captured a structured quantity, where there is nothing
   * to number and no GUID to encode.
   *
   * @param populatedLabel - The label template with its {@link populateTemplate}
   *   placeholders already filled. Populated ONCE and reused for every item, so
   *   that method's `{{key}}` regex never runs over the spliced SVG markup.
   * @param uniformQuantity - The whole-document quantity, used only on the legacy
   *   no-item-rows path.
   * @param itemContext - The container rows, per-item copies, and app base URL.
   * @returns The document body for the print iframe.
   */
  /**
   * The log's item rows that actually carry an item id — the only rows that
   * can be numbered or QR-encoded. Shared by {@link usesQrSheet} and
   * {@link generatePrintContent} so the two never disagree about which rows
   * "count".
   */
  private validItemRows(itemContext?: BrowserLabelItems): ReceivingItemRef[] {
    return (itemContext?.items ?? []).filter(
      (row) => !!row?.itemId && row.itemId.trim().length > 0,
    );
  }

  /**
   * Whether a full-sheet print job should use the QR-carrying sheet template
   * (sheet/qr-large-label-*) rather than its QR-less twin
   * (sheet/large-label-*): item rows exist AND this facility has browser QR
   * turned on. A legacy log with no item rows has nothing to encode or number
   * per-container, so it always gets the plain sheet regardless of the QR
   * setting.
   */
  private usesQrSheet(itemContext?: BrowserLabelItems): boolean {
    return this.validItemRows(itemContext).length > 0 && itemContext?.includeQr !== false;
  }

  private generatePrintContent(
    populatedLabel: string,
    uniformQuantity: number,
    itemContext?: BrowserLabelItems,
    alwaysCountCaption: boolean = false,
  ): string {
    const rows = this.validItemRows(itemContext);

    if (rows.length === 0) {
      // Legacy: the same label repeated. The per-item slots are cleared rather
      // than left alone, or their placeholders print as literal text.
      const count = Math.max(0, Math.floor(uniformQuantity) || 0);
      return this.joinWithPageBreaks(
        Array.from({ length: count }, () => this.clearItemSlots(populatedLabel)),
      );
    }

    const copies = normalizeCopies(itemContext?.copies, rows.length);
    // An item set to zero copies is omitted — that is how an operator reprints
    // only the one sticker they lost instead of the whole pallet, and it matches
    // what the print API path does with a zeroed item.
    const includeQr = itemContext?.includeQr !== false;
    const labels = rows.flatMap((row, i) =>
      Array.from({ length: copies[i] }, () =>
        this.fillItemSlots(populatedLabel, row, itemContext!.appBaseUrl, includeQr, alwaysCountCaption),
      ),
    );
    return this.joinWithPageBreaks(labels);
  }

  /**
   * Joins finished labels into one document, page-breaking BETWEEN them — a break
   * after the last one would eject a blank page, and on a 3in x 1in roll that is
   * a wasted label.
   */
  private joinWithPageBreaks(labels: string[]): string {
    return labels.join('<div style="page-break-after: always;"></div>');
  }

  /**
   * Fills one label's per-item slots: its QR code and its caption.
   *
   * Both replacements go through a function so a `$` in the spliced markup cannot
   * be read as a capture-group reference.
   *
   * @param includeQr - Whether this facility prints QR codes on browser labels.
   *   False still fills the caption: the container is numbered either way.
   * @param alwaysCountCaption - Use {@link itemCaptionAlwaysCounted} instead of
   *   {@link itemCaption}, so a lone item of its type still reads "TUBE 1 of 1"
   *   rather than just "TUBE". Set only for the QR-carrying copier sheet, whose
   *   caption sits beside a QR code rather than next to other lines that
   *   already state the count.
   */
  private fillItemSlots(
    label: string,
    item: ReceivingItemRef,
    appBaseUrl: string,
    includeQr: boolean,
    alwaysCountCaption: boolean = false,
  ): string {
    // An empty slot is not a gap: both stylesheets collapse their QR box with an
    // `:empty` rule, so the sheet reclaims the space rather than printing a hole.
    const qrSvg = includeQr ? this.renderQrSvg(appBaseUrl, item.itemId) : '';
    const caption = escapeLabelText(alwaysCountCaption ? itemCaptionAlwaysCounted(item) : itemCaption(item));
    return label
      .replace(ITEM_QR_SLOT, () => qrSvg)
      .replace(CONTAINER_CAPTION_SLOT, () => caption);
  }

  /** Empties the per-item slots for a legacy label that has neither. */
  private clearItemSlots(label: string): string {
    return label.replace(ITEM_QR_SLOT, '').replace(CONTAINER_CAPTION_SLOT, '');
  }

  /**
   * Renders one item's QR code as inline SVG markup, ready to splice into a label.
   *
   * SVG rather than a raster image because it stays sharp at label size, which is
   * what makes it scan off shrink wrap. The encoded URL is built by
   * {@link buildQrUrl} — the same function the print API path uses — so a
   * browser-printed label scans exactly like an API-printed one.
   *
   * Two corrections are applied to what `BrowserQRCodeSvgWriter` emits: it sets a
   * fixed pixel width/height and NO `viewBox`, so sizing it from the stylesheet
   * would crop the code instead of scaling it. We add the viewBox and drop the
   * fixed dimensions, which puts the printed size entirely in the label sheet's
   * calibration block. (The writer also assigns width from the height argument
   * and vice versa — harmless here because the request is always square.)
   *
   * A failure returns empty markup rather than throwing: a label with no QR still
   * carries the job number, PO and caption, and beats no label at all. The URL is
   * built INSIDE that guard, not passed in already built — a missing base URL
   * used to throw out through `generatePrintContent` and abort the whole print
   * job, so one unconfigured environment key cost the operator every label
   * instead of the QR square on them.
   */
  private renderQrSvg(appBaseUrl: string, itemGuid: string): string {
    try {
      const qrUrl = buildQrUrl(appBaseUrl, itemGuid);
      const px = PrintService.QR_SOURCE_PX;
      const svg = new BrowserQRCodeSvgWriter().write(qrUrl, px, px);
      const width = svg.getAttribute('width') ?? `${px}`;
      const height = svg.getAttribute('height') ?? `${px}`;
      svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
      svg.removeAttribute('width');
      svg.removeAttribute('height');
      // Serialized into an HTML document, where the parser infers the SVG
      // namespace anyway — set explicitly so the markup is valid standalone.
      svg.setAttribute('xmlns', 'http://www.w3.org/2000/svg');
      return svg.outerHTML;
    } catch (e) {
      console.error('Could not render the item QR code; printing this label without one.', e);
      return '';
    }
  }


   /**
   * Prints the generated content inside a hidden iframe for proper layout.
   * @param content - The HTML content to print.
   * @param styles - The CSS styles to apply to the print content.
   * @param isDymoLabel - Optional flag to indicate if it's a Dymo label (for special handling).
   */

  private printContentInIframe(content: string, styles: string, isDymoLabel:boolean=false): void {
    const iframe = this.createHiddenIframe();

    const iframeDoc = iframe.contentDocument || iframe.contentWindow?.document;
    if (iframeDoc) {
      iframeDoc.open();
      iframeDoc.write(this.buildIframeDocument(content, styles));
      iframeDoc.close();

            // Print once, from whichever of the two triggers below arrives first.
      //
      // The load event alone was not enough. It is attached AFTER open/write/close
      // above, so a document that finishes loading inside that window fires it
      // before there is a handler to hear it — and then nothing prints, with no
      // dialog, no error and no log, which is exactly how a copier sheet went
      // missing while the label beside it printed. Whether the race is lost
      // depends on what else is on the main thread, which is why it showed up on
      // a split job (a print API call fires first) and not on a browser-only one.
      let hasPrinted = false;
      const printOnce = (viaFallback: boolean): void => {
        if (hasPrinted) { return; }
        hasPrinted = true;
        if (viaFallback) {
          console.warn('Print iframe load event never arrived; printing on the fallback timer.');
        }
        if (iframe.contentWindow?.document.readyState === 'complete' && !isDymoLabel) {
          this.fitLabelsToPage(iframeDoc); // Fit full-sheet labels to the page.
        }
        iframe.contentWindow?.focus();
        iframe.contentWindow?.print();
        this.cleanUpIframe(iframe); //Clean up iframe after printing.
      };

      iframe.onload = () => printOnce(false);
      // The safety net. Long enough that a normal load wins it, short enough that
      // the dialog still feels like a response to the click that asked for it.
      setTimeout(() => printOnce(true), PrintService.PRINT_FALLBACK_MS);


    } else {
      console.error('Failed to create print document in iframe.');
    }
  }

  /**
   * Builds the iframe document with the provided content and styles.
   * @param content - The HTML content to include in the iframe.
   * @param styles - The CSS styles to apply to the content.
   * @returns A string representing the complete HTML document.
   */

  private buildIframeDocument(content: string, styles: string): string {
    return `
      <html>
        <head>
          <title>Print Label</title>
          <style>${styles}</style>
        </head>
        <body>
          ${content}
        </body>
      </html>
    `;
  }

  /**
   * Creates a hidden iframe for printing.
   *
   * Sized to a US Letter page rather than left at the browser's 300x150 default,
   * because {@link fitLabelsToPage} MEASURES this document before printing it. At
   * the default size `100vh` is 150px, so a full-sheet label reports a height of
   * about 30px and every measurement taken against it is meaningless — which is
   * why the width pass below used to compare against a hardcoded 900 instead of
   * asking the element. With the iframe at page size those numbers are the ones
   * the printer will use.
   *
   * @returns The created iframe element.
   */
  private createHiddenIframe(): HTMLIFrameElement {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.top = '-10000px'; // Hide iframe off-screen.
    iframe.style.border = '0';
    iframe.style.width = `${PrintService.PAGE_WIDTH_PX}px`;
    iframe.style.height = `${PrintService.PAGE_HEIGHT_PX}px`;
    document.body.appendChild(iframe);
    return iframe;
  }



  /**
   * Makes every full-sheet label fit its page, WIDTH AND HEIGHT.
   *
   * Height is the half that was missing, and its absence is why the QR code
   * stopped printing: `.label` is a fixed-height `overflow: hidden` box, the QR
   * square is its LAST child, and the per-container work added both a 3in square
   * and an oversized caption line to a page that was already full. Everything
   * past the bottom edge was silently cropped, and what sits past the bottom edge
   * is exactly the one thing on the sheet that has to be scannable.
   *
   * The stylesheet's calibration knobs stay the starting sizes — they say how big
   * this should print when it fits. This pass only ever shrinks, and only when it
   * does not:
   *
   *   1. Text first, down to {@link MIN_LINE_PX}. Losing a few points off a
   *      110px line costs nothing anyone reads across a warehouse.
   *   2. The QR second, and only if the text floor was not enough, down to
   *      {@link MIN_QR_IN}. A code smaller than that stops scanning reliably off
   *      a phone camera, at which point printing it at all is theatre.
   *
   * Per `.label` rather than per line, because each label is its own page and a
   * short one should not be shrunk to match a long one.
   *
   * @param iframeDoc - The document object of the iframe.
   */
  private fitLabelsToPage(iframeDoc: Document): void {
    const view = iframeDoc.defaultView;
    if (!view) { return; }

    iframeDoc.querySelectorAll('.label').forEach((node) => {
      const label = node as HTMLElement;
      this.fitLabelWidth(label, view);
      this.fitLabelHeight(label, view);
    });
  }

  /**
   * Shrinks any line whose text is wider than the page it prints on.
   *
   * The lines are `white-space: nowrap`, so a long job name does not wrap — it
   * runs off the side of the paper unless it is scaled down.
   */
  private fitLabelWidth(label: HTMLElement, view: Window): void {
    const maxWidth = label.clientWidth || PrintService.PAGE_WIDTH_PX;

    label.querySelectorAll('.label-line').forEach((node) => {
      const element = node as HTMLElement;
      let fontSize = parseFloat(view.getComputedStyle(element).fontSize);

      while (element.scrollWidth > maxWidth && fontSize > PrintService.MIN_LINE_PX) {
        fontSize -= 1;
        element.style.fontSize = `${fontSize}px`;
      }
    });
  }

  /**
   * Shrinks the label until its content stops overflowing the page box.
   *
   * `scrollHeight` still reports the true content height under `overflow: hidden`
   * — the box clips what it cannot show, it does not forget it is there — so this
   * can see the cropping the printed page cannot.
   *
   * Both loops carry an iteration guard. The measurements come from a live layout
   * that a stylesheet edit could make unsatisfiable (a `min-height` on a line, say),
   * and a print path that hangs the tab is worse than one that prints slightly too big.
   */
  private fitLabelHeight(label: HTMLElement, view: Window): void {
    const overflows = (): boolean => label.scrollHeight > label.clientHeight;
    if (!label.clientHeight || !overflows()) { return; }

    // Sizes are read ONCE, up front. Re-reading the computed style each step
    // would re-measure a layout this loop is itself changing, which is both
    // slower and a way to stall on a value that never quite settles.
    const lines = (Array.from(label.querySelectorAll('.label-line')) as HTMLElement[])
      .map((element) => ({ element, size: parseFloat(view.getComputedStyle(element).fontSize) }))
      .filter((line) => Number.isFinite(line.size));

    for (let guard = 0; guard < 200 && overflows(); guard++) {
      const shrunk = lines.filter((line) => {
        const next = line.size - PrintService.LINE_STEP_PX;
        if (next < PrintService.MIN_LINE_PX) { return false; }
        line.size = next;
        line.element.style.fontSize = `${next}px`;
        return true;
      });
      if (shrunk.length === 0) { break; }
    }

    const qr = label.querySelector('.sheet-qr') as HTMLElement | null;
    if (!qr) { return; }
    // In inches, matching the stylesheet knob, so a future calibration edit reads
    // in the same unit the shrink steps down in.
    let inches = parseFloat(view.getComputedStyle(qr).width) / PrintService.CSS_PX_PER_IN;
    for (let guard = 0; guard < 40 && overflows(); guard++) {
      const next = inches - PrintService.QR_STEP_IN;
      if (!Number.isFinite(next) || next < PrintService.MIN_QR_IN) { break; }
      inches = next;
      qr.style.width = `${next}in`;
      qr.style.height = `${next}in`;
    }
  }


  /**
   * Removes the iframe from the DOM after printing to clean up resources.
   * @param iframe - The iframe element to remove.
   */

  private cleanUpIframe(iframe: HTMLIFrameElement): void {
    setTimeout(() => {
      document.body.removeChild(iframe);
    }, 1000);
  }


  /**
   * Resets the `isPrintingLargeLabel` flag after a short delay to allow subsequent printing.
   */

  private resetLargeLabelPrintingFlagAfterDelay(): void {
    setTimeout(() => {
      this.isPrintingLargeLabel = false;
    }, 1000);
  }


  /**
   * Resets the `isPrintingDymoLabel` flag after a short delay to allow subsequent printing.
   */

    private resetDymoLabelPrintingFlagAfterDelay(): void {
      setTimeout(() => {
        this.isPrintingDymoLabel = false;
      }, 1000);
    }

  /**
   * Generates preview content for a Dymo label
   * @param labelData The data to be inserted into the label template
   * @returns An object containing the HTML content and styles
   */
  public generateDymoPreview(labelData: NewLog): { content: string, styles: string } {
    const {css, html} = this._assetsDataService.GetDymoLabelAssets();
    const labelTemplate = html;
    const labelStyles = css;
    
    const printHtmlPage = this.populateTemplate(labelTemplate, labelData, true);
    return { content: printHtmlPage, styles: labelStyles };
  }

  /**
   * Generates preview content for a large label
   * @param labelData The data to be inserted into the label template
   * @returns An object containing the HTML content and styles
   */
  public generateLargeLabelPreview(labelData: NewLog): { content: string, styles: string } {
    const {css, html} = this._assetsDataService.GetFullSheetAssets();
    const labelTemplate = html;
    const labelStyles = css;
    
    const printHtmlPage = this.populateTemplate(labelTemplate, labelData);
    return { content: printHtmlPage, styles: labelStyles };
  }

  /**
   * Generates preview content for both Dymo and large labels based on the print type
   * @param labelData The data to be inserted into the label templates
   * @returns An object containing the preview content and their quantities
   */
  public generatePreviews(labelData: NewLog): { 
    dymo?: { content: string, styles: string }, 
    large?: { content: string, styles: string }, 
    dymoQty: number, 
    largeQty: number 
  } {
    const result: { 
      dymo?: { content: string, styles: string }, 
      large?: { content: string, styles: string }, 
      dymoQty: number, 
      largeQty: number 
    } = {
      dymoQty: 0,
      largeQty: 0
    };

    if (labelData.printType === 'Label' || labelData.printType === 'Both') {
      result.dymo = this.generateDymoPreview(labelData);
      result.dymoQty = labelData.printQtyDymo;
    }

    if (labelData.printType === 'Copier Sheet' || labelData.printType === 'Both') {
      result.large = this.generateLargeLabelPreview(labelData);
      result.largeQty = labelData.printQtySheet;
    }

    return result;
  }
}
